import { Controller } from "@hotwired/stimulus"
import { replayAnimation } from "../lib/motion"
import { formatLivePrice } from "../lib/live_price"

// UI::WizardLiveCardComponent: the real public card (inert) mirrors the step's inputs as the user types. Inputs map
// to the card's [data-live-slot] title / price / meta by name; the thumb slot follows the step's upload grid — its
// first preview, through adds, removals, reorders and late-loading previews — and shows the empty placeholder when
// no photo is left. A slot that goes from empty to filled pops the card once.
export default class extends Controller {
  static values = { fields: Object, priceFormat: Object, placeholder: String, kind: String }

  connect() {
    // Slots the draft already fills count as filled (no pop on the first keystroke); keep their text as the fallback.
    this.element.querySelectorAll("[data-live-slot]").forEach((slot) => {
      slot.dataset.liveOriginal ||= slot.textContent.trim()
    })
    const title = this.slot("title")
    if (title && title.textContent.trim() !== this.placeholderValue) title.dataset.liveFilled = "true"
    this.form = this.element.closest("form")
    if (!this.form) return
    this.onInput = this.onInput.bind(this)
    this.form.addEventListener("input", this.onInput)
    this.form.addEventListener("change", this.onInput)
    this.watchPhotos()
  }

  watchPhotos() {
    this.grid = this.form.querySelector('[data-direct-upload-target="preview"]')
    if (!this.grid) return
    this.thumbSrc = this.firstPhotoSrc()
    this.gridObserver = new MutationObserver(() => {
      if (this.frame) return
      this.frame = requestAnimationFrame(() => { this.frame = null; this.syncThumb() })
    })
    this.gridObserver.observe(this.grid, { childList: true, subtree: true, attributes: true, attributeFilter: [ "src" ] })
  }

  firstPhotoSrc() {
    const img = this.grid?.querySelector(".direct-upload-preview img, [data-direct-upload='preview'] img")
    const src = img?.currentSrc || img?.getAttribute("src") || null
    // direct_upload_controller's broken-image fallback is an inline SVG, not a photo
    return src && !src.startsWith("data:image/svg") ? src : null
  }

  syncThumb() {
    const thumb = this.slot("thumb")
    const src = this.firstPhotoSrc()
    if (!thumb || src === this.thumbSrc) return
    const wasEmpty = !this.thumbSrc
    this.thumbSrc = src

    if (src) {
      const img = document.createElement("img")
      img.src = src
      img.alt = ""
      img.className = "h-full w-full object-cover"
      thumb.replaceChildren(img)
      if (wasEmpty) replayAnimation(this.element, "is-updated")
    } else {
      const empty = this.element.querySelector("template[data-live-thumb-empty]")
      thumb.replaceChildren(...(empty ? empty.content.cloneNode(true).childNodes : []))
    }
  }

  disconnect() {
    this.form?.removeEventListener("input", this.onInput)
    this.form?.removeEventListener("change", this.onInput)
    this.gridObserver?.disconnect()
    if (this.frame) cancelAnimationFrame(this.frame)
  }

  slot(name) {
    return this.element.querySelector(`[data-live-slot="${name}"]`)
  }

  onInput(event) {
    const input = event.target

    const name = this.fieldsValue[input.name]
    const value = input.value.trim()
    if (name === "title") this.write(this.slot("title"), value || this.placeholderValue, value !== "")
    if (name === "meta") this.write(this.slot("meta"), value || this.slot("meta")?.dataset.liveOriginal || "", value !== "")
    if (name === "price") this.writePrice(input.value)
  }

  write(target, text, filled) {
    if (!target) return
    const wasFilled = target.dataset.liveFilled === "true"
    target.textContent = text
    target.dataset.liveFilled = String(filled)
    if (filled && !wasFilled) replayAnimation(this.element, "is-updated")
  }

  writePrice(raw) {
    const target = this.slot("price")
    if (!target) return
    const digits = String(raw ?? "").replace(/[^\d]/g, "")
    if (digits === "") {
      this.element.dataset.priceEmpty = "true"
      return
    }

    delete this.element.dataset.priceEmpty
    const { prefix = "", zero } = this.priceFormatValue
    const isZero = Number(digits) === 0
    // The product card shows a free item as 免費, bigger and in green (Products::CardComponent).
    if (this.kindValue === "product") {
      target.classList.toggle("text-green-600", isZero)
      target.classList.toggle("text-2xl", isZero)
      target.classList.toggle("text-crust-600", !isZero)
      target.classList.toggle("text-xl", !isZero)
    }
    this.write(target, isZero && zero ? zero : formatLivePrice(digits, prefix), true)
  }
}
